import React from 'react'
import { Box, Divider, Typography } from '@mui/material'

import DualActionButtons from '../../../../components/action_button/DualActionButtons'
import DownloadCsvPdfSplitButton from '../../../../components/action_button/DownloadCsvPdfSplitButton'
import { formatQuantity } from '../../../../helpers/utils'
import { REFUND_APPROVAL_TEXT } from '../../../constants/refundApprovalText'
import { UI_TEXT_DOWNLOADING } from '../../../../constants/uiText'
import {
  UI_COLOR_ACCENT_ORANGE,
  UI_COLOR_ACCENT_ORANGE_ALT_HOVER,
  UI_COLOR_BORDER_DIVIDER,
  UI_COLOR_DANGER_ACTION,
  UI_COLOR_DANGER_ACTION_HOVER,
  UI_COLOR_PRIMARY_ALT,
  UI_COLOR_PRIMARY_SOFT_ALT,
  UI_COLOR_TEXT_WARM,
  UI_COLOR_WHITE
} from '../../../../constants/uiColors'
import RefundNcrActionButton from '../ncr/RefundNcrActionButton'

const RefundApprovalDetailActions = ({
  canWrite,
  canRegisterNcr,
  isFinalized,
  loading,
  canApproveCount,
  showLineActions,
  allLinesRejected,
  processedLinesCount,
  processingLinesCount,
  isGeneratingPdf,
  onRejectClick,
  onApproveClick,
  onNcrClick,
  onExportCsv,
  onExportPdf,
  fullWidthOnMobile = false
}) => (
  <Box
    sx={{
      position: 'sticky',
      top: 0,
      zIndex: 20,
      mx: { xs: -2.75, md: -7 },
      px: { xs: 2.75, md: 7 },
      py: fullWidthOnMobile ? 1 : 0,
      bgcolor: UI_COLOR_WHITE,
      mb: 2,
      boxSizing: 'border-box',
      '@media print': {
        position: 'static'
      }
    }}
  >
    <Box
      sx={{
        display: 'flex',
        alignItems: fullWidthOnMobile ? 'stretch' : 'flex-start',
        justifyContent: 'space-between',
        flexDirection: fullWidthOnMobile ? 'column' : 'row',
        columnGap: fullWidthOnMobile ? 1.25 : 2,
        rowGap: fullWidthOnMobile ? 1.5 : 2.5,
        flexWrap: 'wrap'
      }}
    >
      <Typography
        sx={{
          fontSize: fullWidthOnMobile ? 14 : 16,
          lineHeight: fullWidthOnMobile ? 1.3 : '70px'
        }}
      >
        {REFUND_APPROVAL_TEXT.detailsHeading}
      </Typography>

      {canWrite && !isFinalized && (
        <Box
          sx={{
            display: 'flex',
            alignItems: fullWidthOnMobile ? 'stretch' : 'flex-end',
            flexDirection: 'column',
            gap: 1,
            mt: fullWidthOnMobile ? 0 : 2,
            width: fullWidthOnMobile ? '100%' : 'auto'
          }}
        >
          <DualActionButtons
            fullWidthOnMobile={fullWidthOnMobile}
            equalWidthOnMobile={fullWidthOnMobile}
            minWidth={fullWidthOnMobile ? 0 : 150}
            secondaryAction={{
              label: REFUND_APPROVAL_TEXT.reject,
              icon: 'close',
              color: UI_COLOR_DANGER_ACTION,
              hoverColor: UI_COLOR_DANGER_ACTION_HOVER,
              onClick: onRejectClick,
              disabled: loading,
              fullWidth: false
            }}
            primaryAction={{
              label: REFUND_APPROVAL_TEXT.approve,
              icon: 'check_circle',
              iconFilled: true,
              color: UI_COLOR_ACCENT_ORANGE,
              hoverColor: UI_COLOR_ACCENT_ORANGE_ALT_HOVER,
              onClick: onApproveClick,
              disabled: loading || !canApproveCount,
              visible: true,
              fullWidth: false
            }}
          />
          {showLineActions && (
            <Typography
              sx={{
                fontSize: 12,
                color: UI_COLOR_TEXT_WARM,
                textAlign: fullWidthOnMobile ? 'center' : 'left',
                width: fullWidthOnMobile ? '100%' : 'auto'
              }}
            >
              {allLinesRejected
                ? REFUND_APPROVAL_TEXT.allLinesRejected
                : `${formatQuantity(processedLinesCount)} ${REFUND_APPROVAL_TEXT.linesVerifiedOf} ${formatQuantity(
                    processingLinesCount
                  )} ${REFUND_APPROVAL_TEXT.linesVerifiedSuffix}`}
            </Typography>
          )}
        </Box>
      )}

      {canRegisterNcr && !isFinalized && (
        <Box sx={{ mt: fullWidthOnMobile ? 0 : 2, width: fullWidthOnMobile ? '100%' : 'auto' }}>
          <RefundNcrActionButton
            fullWidthOnMobile={fullWidthOnMobile}
            loading={loading}
            onClick={onNcrClick}
          />
        </Box>
      )}

      {isFinalized && (
        <Box
          sx={{
            display: fullWidthOnMobile ? 'grid' : 'flex',
            gridTemplateColumns:
              fullWidthOnMobile && canRegisterNcr ? 'minmax(0, 1fr) minmax(0, 1fr)' : '1fr',
            alignItems: fullWidthOnMobile ? 'stretch' : 'flex-end',
            flexDirection: fullWidthOnMobile ? undefined : 'row',
            gap: 1,
            mt: fullWidthOnMobile ? 0 : 2,
            width: fullWidthOnMobile ? '100%' : 'auto',
            minWidth: 0,
            '& .MuiButtonGroup-root': {
              width: fullWidthOnMobile ? '100%' : 'auto',
              minWidth: 0,
              display: 'flex'
            },
            '& .download-doble-reports': {
              flex: fullWidthOnMobile ? 1 : undefined,
              minWidth: fullWidthOnMobile ? 0 : undefined,
              width: fullWidthOnMobile ? 'auto !important' : undefined,
              height: fullWidthOnMobile ? '44px !important' : undefined,
              px: fullWidthOnMobile ? 1 : undefined,
              fontSize: fullWidthOnMobile ? '13px !important' : undefined
            },
            '& .download-doble-reports-arrow': {
              width: fullWidthOnMobile ? 42 : undefined,
              minWidth: fullWidthOnMobile ? 42 : undefined,
              flexShrink: fullWidthOnMobile ? 0 : undefined,
              height: fullWidthOnMobile ? '44px !important' : undefined
            },
            '& .download-doble-reports .MuiButton-startIcon': {
              mr: fullWidthOnMobile ? 0.75 : undefined,
              ml: 0
            }
          }}
        >
          <DownloadCsvPdfSplitButton
            label={isGeneratingPdf ? UI_TEXT_DOWNLOADING : undefined}
            pdfLabel={isGeneratingPdf ? UI_TEXT_DOWNLOADING : REFUND_APPROVAL_TEXT.downloadPdf}
            color={UI_COLOR_PRIMARY_SOFT_ALT}
            hoverColor={UI_COLOR_PRIMARY_ALT}
            csvLabel={REFUND_APPROVAL_TEXT.downloadCsv}
            onExportCsv={onExportCsv}
            onExportPdf={onExportPdf}
            disabled={processingLinesCount === 0 || isGeneratingPdf}
          />
          {canRegisterNcr && (
            <RefundNcrActionButton
              fullWidthOnMobile={fullWidthOnMobile}
              loading={loading}
              onClick={onNcrClick}
            />
          )}
        </Box>
      )}

      <Divider
        sx={{
          flexBasis: '100%',
          width: '100%',
          border: 0,
          borderTop: `1px solid ${UI_COLOR_BORDER_DIVIDER}`
        }}
      />
    </Box>
  </Box>
)

export default RefundApprovalDetailActions
